/**
 * Riconoscimento effettivo del dispositivo: Wear OS / smartwatch via User-Agent,
 * dimensioni schermo/finestra e altri segnali browser.
 */

/** Lato minore max (px) per considerare schermo da orologio */
const WATCH_MIN_SIDE = 480
/** Lato maggiore max (px): su uno smartwatch entrambi i lati sono piccoli; su phone uno è grande */
const WATCH_MAX_SIDE = 540

/**
 * True solo se schermo/finestra hanno ENTRAMBE le dimensioni piccole (tipico orologio ~quadrato).
 * Su smartphone in portrait il lato maggiore è alto (es. 360×800) → non orologio.
 */
function isSmallWatchLikeScreen() {
  function check(w, h) {
    if (!w || !h) return false
    const min = Math.min(w, h)
    const max = Math.max(w, h)
    return min <= WATCH_MIN_SIDE && max <= WATCH_MAX_SIDE
  }
  if (typeof screen !== 'undefined') {
    const w = screen.width || screen.availWidth || 0
    const h = screen.height || screen.availHeight || 0
    if (check(w, h)) return true
  }
  if (typeof window !== 'undefined' && window.outerWidth != null && window.outerHeight != null) {
    if (check(window.outerWidth, window.outerHeight)) return true
  }
  return false
}

export function isWearOSDevice() {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return false
  const ua = navigator.userAgent
  // Wear OS, Galaxy Watch, modelli Samsung SM-Rxxx (orologi), UA tipici da watch
  if (/Wear\s*OS|WearOS/i.test(ua)) return true
  if (/Galaxy\s*Watch|Samsung\s*Watch/i.test(ua)) return true
  if (/SM-R\d{2,4}/i.test(ua)) return true // Samsung wearable (SM-R90, SM-R900, …)
  if (/Android.*(?:Wear|Watch)/i.test(ua)) return true
  if (/Mobile.*(?:Wear|Watch)|(?:Wear|Watch).*Mobile/i.test(ua)) return true
  // SamsungBrowser su watch: "(Linux; Android 10; K)" o "Android 10.0; K"
  if (/Android\s+\d+(?:\.\d+)?;\s*K\s*\)/.test(ua) && /SamsungBrowser/i.test(ua)) return true
  // Fallback: Android + schermo/finestra piccoli (orologio anche con UA generico)
  if (/Android/i.test(ua) && isSmallWatchLikeScreen()) return true
  return false
}

/**
 * Schermo molto piccolo (es. 1.73"): usa viewport o screen come fallback solo se già su watch.
 */
export function isTinyWatchScreen() {
  if (typeof window === 'undefined') return false
  if (window.matchMedia && window.matchMedia('(max-width: 300px)').matches) return true
  if (typeof window.screen !== 'undefined' && window.screen.width <= 320) return true
  return false
}

/**
 * Nome amichevole del dispositivo per il welcome popup (da User-Agent).
 * Usa gli stessi criteri di isWearOSDevice() per gli orologi, così il watch
 * non viene mai mostrato come "Android". Se isWearOSDevice() è true ma l’UA
 * non matcha i pattern (es. UA semplificato), restituisce comunque "Wear OS".
 */
export function getDeviceDisplayName() {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return 'Device'
  const ua = navigator.userAgent
  // Wear OS / Galaxy Watch: stessi pattern di isWearOSDevice(), prima di "Android"
  if (/Galaxy\s*Watch|Samsung\s*Watch/i.test(ua)) return 'Galaxy Watch'
  if (/Wear\s*OS|WearOS/i.test(ua)) return 'Wear OS'
  if (/SM-R\d{2,4}/i.test(ua)) return 'Galaxy Watch' // SM-R90, SM-R900, SM-R875U…
  if (/Android.*(?:Wear|Watch)|(?:Wear|Watch).*Android/i.test(ua)) return 'Wear OS'
  if (/Mobile.*(?:Wear|Watch)|(?:Wear|Watch).*Mobile/i.test(ua)) return 'Wear OS'
  if (/Android\s+\d+(?:\.\d+)?;\s*K\s*\)/.test(ua) && /SamsungBrowser/i.test(ua)) return 'Galaxy Watch'
  // Prima di qualsiasi "Android": se isWearOSDevice() è true (UA o schermo piccolo), è un orologio
  if (isWearOSDevice()) return 'Wear OS'
  // Altri dispositivi
  if (/iPhone|iPad/i.test(ua)) return /iPad/i.test(ua) ? 'iPad' : 'iPhone'
  if (/Android/i.test(ua) && /Mobile/i.test(ua)) return 'Android'
  if (/Windows/i.test(ua)) return 'Windows'
  if (/Mac\s*OS|Macintosh/i.test(ua)) return 'Mac'
  if (/Linux/i.test(ua)) return 'Linux'
  return 'Device'
}

/**
 * Icona emoji in base al tipo di dispositivo (per welcome popup).
 * Se isWearOSDevice() è true restituisce sempre ⌚ (stesso fallback del nome).
 */
export function getDeviceIcon() {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return '🖥️'
  const ua = navigator.userAgent
  if (/Galaxy\s*Watch|Samsung\s*Watch|Wear\s*OS|WearOS/i.test(ua)) return '⌚'
  if (/SM-R\d{2,4}/i.test(ua)) return '⌚'
  if (/Android.*(?:Wear|Watch)|(?:Wear|Watch).*Android/i.test(ua)) return '⌚'
  if (/Mobile.*(?:Wear|Watch)|(?:Wear|Watch).*Mobile/i.test(ua)) return '⌚'
  if (/Android\s+\d+(?:\.\d+)?;\s*K\s*\)/.test(ua) && /SamsungBrowser/i.test(ua)) return '⌚'
  if (isWearOSDevice()) return '⌚'
  if (/iPhone|iPad|Android.*Mobile|Mobile.*Android/i.test(ua)) return '📱'
  return '🖥️'
}

/**
 * All data used for device detection + full user agent, for the debug "Detect device" panel.
 */
export function getDeviceDebugInfo() {
  const ua = typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : ''
  const isWearOS = isWearOSDevice()
  const isTiny = isTinyWatchScreen()
  const smallWatchLike = isSmallWatchLikeScreen()
  let screenWidth = null
  let screenHeight = null
  let availWidth = null
  let availHeight = null
  let outerWidth = null
  let outerHeight = null
  let innerWidth = null
  let innerHeight = null
  let matchMedia300 = null
  if (typeof screen !== 'undefined') {
    screenWidth = screen.width ?? screen.availWidth ?? null
    screenHeight = screen.height ?? screen.availHeight ?? null
    availWidth = screen.availWidth ?? null
    availHeight = screen.availHeight ?? null
  }
  if (typeof window !== 'undefined') {
    outerWidth = window.outerWidth ?? null
    outerHeight = window.outerHeight ?? null
    innerWidth = window.innerWidth ?? null
    innerHeight = window.innerHeight ?? null
    if (window.matchMedia) {
      try {
        matchMedia300 = window.matchMedia('(max-width: 300px)').matches
      } catch (_) {
        matchMedia300 = null
      }
    }
  }
  return {
    userAgent: ua,
    displayName: getDeviceDisplayName(),
    icon: getDeviceIcon(),
    isWearOSDevice: isWearOS,
    isTinyWatchScreen: isTiny,
    isSmallWatchLikeScreen: smallWatchLike,
    screenWidth,
    screenHeight,
    availWidth,
    availHeight,
    outerWidth,
    outerHeight,
    innerWidth,
    innerHeight,
    matchMediaMaxWidth300px: matchMedia300,
  }
}
